import { ComponentType } from 'react';

import { Button } from '@components/button';
import { ExpandableSearch } from '@components/expandable-search';
import { IconProps } from '@components/icons';
import { IconAttachment } from '@components/icons/attachment';
import { IconAttachmentMultiple } from '@components/icons/attachment-multiple';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDate } from '@components/icons/date';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconEmail } from '@components/icons/email';
import { IconPerson } from '@components/icons/person';
import { IconPhone } from '@components/icons/phone';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Stack } from '@components/stack';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';

import { AttributeFieldType } from 'api/attributes/requests';

export type FieldTypeCategory = 'inputs' | 'selections';

export const categoryColors: Record<FieldTypeCategory, Color> = {
  inputs: 'elementOrange200',
  selections: 'blue100',
};

export type FieldTypeConfig = {
  labelKey: string;
  descriptionKey: string;
  icon: ComponentType<IconProps>;
  category: FieldTypeCategory;
};

export const fieldTypeConfig: Record<AttributeFieldType, FieldTypeConfig> = {
  text: {
    labelKey: 'singleLineText',
    descriptionKey: 'singleLineTextDescription',
    icon: IconTextShort,
    category: 'inputs',
  },
  paragraph: {
    labelKey: 'longText',
    descriptionKey: 'longTextDescription',
    icon: IconTextLong,
    category: 'inputs',
  },
  number: {
    labelKey: 'number',
    descriptionKey: 'numberDescription',
    icon: IconTextFormatNumber,
    category: 'inputs',
  },
  decimal: {
    labelKey: 'number',
    descriptionKey: 'numberDescription',
    icon: IconDecimalFloat,
    category: 'inputs',
  },
  email: {
    labelKey: 'emailAddress',
    descriptionKey: 'emailAddressDescription',
    icon: IconEmail,
    category: 'inputs',
  },
  phone_number: {
    labelKey: 'phoneNumber',
    descriptionKey: 'phoneNumberDescription',
    icon: IconPhone,
    category: 'inputs',
  },
  date: {
    labelKey: 'date',
    descriptionKey: 'dateDescription',
    icon: IconDate,
    category: 'inputs',
  },
  file: {
    labelKey: 'fileUpload',
    descriptionKey: 'fileUploadDescription',
    icon: IconAttachment,
    category: 'inputs',
  },
  file_multi: {
    labelKey: 'fileUpload',
    descriptionKey: 'fileUploadDescription',
    icon: IconAttachmentMultiple,
    category: 'inputs',
  },
  single_select: {
    labelKey: 'singleChoice',
    descriptionKey: 'singleChoiceDescription',
    icon: IconSelectSingle,
    category: 'selections',
  },
  multi_select: {
    labelKey: 'multipleChoice',
    descriptionKey: 'multipleChoiceDescription',
    icon: IconSelectMultiple,
    category: 'selections',
  },
  user: {
    labelKey: 'selectUser',
    descriptionKey: 'selectUserDescription',
    icon: IconPerson,
    category: 'selections',
  },
  boolean: {
    labelKey: 'boolean',
    descriptionKey: 'booleanDescription',
    icon: IconCheckmark,
    category: 'inputs',
  },
};

// Types to show in the selector (excludes file_multi, decimal, boolean which are variants)
export const selectableFieldTypes: AttributeFieldType[] = [
  'text',
  'paragraph',
  'number',
  'email',
  'phone_number',
  'date',
  'file',
  'single_select',
  'multi_select',
  'user',
];

// Helper to get types by category
export const getFieldTypesByCategory = (category: FieldTypeCategory): AttributeFieldType[] => {
  return selectableFieldTypes.filter((type) => {
    return fieldTypeConfig[type].category === category;
  });
};

type AttributeIconProps = IconProps & {
  type: AttributeFieldType;
};

export const AttributeIcon = ({ type, ...rest }: AttributeIconProps) => {
  const config = fieldTypeConfig[type];

  if (!config) {
    console.error(`Unable to find config for attribute type: ${type}`);
    return null;
  }

  const Icon = config.icon;
  return <Icon {...rest} />;
};

export const AttributeActions = ({
  setSearchStr,
  searchStr,
  onAddAttribute,
  isTravellerAttribute,
}: {
  setSearchStr: (searchStr: string) => void;
  searchStr: string;
  onAddAttribute: () => void;
  isTravellerAttribute?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={setSearchStr} defaultValue={searchStr} />
      <Button
        variant="primary"
        text={t('addAttribute')}
        iconLeft={isTravellerAttribute ? <IconAttributeTraveller /> : <IconAttributeJourney />}
        onClick={onAddAttribute}
      />
    </Stack>
  );
};

const locale = {
  en: {
    addAttribute: 'Create field',
    singleLineText: 'Single line text',
    singleLineTextDescription: 'Small general input field',
    longText: 'Long text',
    longTextDescription: 'Large input field',
    number: 'Number',
    numberDescription: 'Small input field for numbers',
    emailAddress: 'Email address',
    emailAddressDescription: 'For adding additional emails',
    phoneNumber: 'Phone number',
    phoneNumberDescription: 'For adding phone numbers',
    date: 'Date',
    dateDescription: 'For selecting dates',
    fileUpload: 'File upload',
    fileUploadDescription: 'For file attachments',
    singleChoice: 'Single choice',
    singleChoiceDescription: 'For selecting a single option from a list',
    multipleChoice: 'Multiple choice',
    multipleChoiceDescription: 'For selecting multiple options from a list',
    selectUser: 'User',
    selectUserDescription: 'For selecting users in the system',
    boolean: 'On / off',
    booleanDescription: 'For selecting a boolean value',
    numberDecimal: 'Number (Decimal)',
  },
};

const { t: mapTranslate } = getScopedTranslate(locale);

export const attributeTextMap: Record<AttributeFieldType, string> = {
  date: mapTranslate('date'),
  text: mapTranslate('singleLineText'),
  paragraph: mapTranslate('longText'),
  user: mapTranslate('selectUser'),
  phone_number: mapTranslate('phoneNumber'),
  email: mapTranslate('emailAddress'),
  boolean: mapTranslate('boolean'),
  single_select: mapTranslate('singleChoice'),
  multi_select: mapTranslate('multipleChoice'),
  file: mapTranslate('fileUpload'),
  file_multi: mapTranslate('fileUpload'),
  number: mapTranslate('number'),
  decimal: mapTranslate('numberDecimal'),
};
